import React, { useState } from 'react';
import {
    buildIcs,
    downloadIcs,
    googleCalendarUrl,
    outlookCalendarUrl,
    outlookOfficeUrl,
    yahooCalendarUrl
} from './eventsPublic';

const TRIGGER_CLASS =
    "border border-gray-300 bg-offwhite px-3 py-1 font-['Jersey_10'] text-[16px] uppercase leading-none tracking-[1px] text-gray-700 shadow-[0_2px_0_0_rgba(0,0,0,0.2)] transition-colors hover:bg-gold";

function CalendarGlyph() {
    return (
        <svg
            width="17"
            height="16"
            viewBox="0 0 17 16"
            fill="currentColor"
            shapeRendering="crispEdges"
            aria-hidden="true"
        >
            <rect x="3" y="0" width="2" height="3" />
            <rect x="9" y="0" width="2" height="3" />
            <rect x="0" y="2" width="14" height="2" />
            <rect x="0" y="4" width="2" height="10" />
            <rect x="12" y="4" width="2" height="10" />
            <rect x="0" y="12" width="14" height="2" />
            <rect x="3" y="6" width="2" height="2" />
            <rect x="6" y="6" width="2" height="2" />
            <rect x="9" y="6" width="2" height="2" />
            <rect x="3" y="9" width="2" height="2" />
            <rect x="10" y="7" width="6" height="10" fill="#F8F9FA" />
            <rect x="8" y="9" width="10" height="6" fill="#F8F9FA" />
            <rect x="11" y="8" width="4" height="8" fill="#1ea03c" />
            <rect x="9" y="10" width="8" height="4" fill="#1ea03c" />
        </svg>
    );
}

const detectPlatform = () => {
    if (typeof navigator === 'undefined') return 'desktop';

    const agent = navigator.userAgent || '';
    const touchMac = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

    if (/iPad|iPhone|iPod/.test(agent) || touchMac) return 'ios';
    if (/Android/i.test(agent)) return 'android';

    return 'desktop';
};

const openIcsInApp = (event) => {
    const body = buildIcs(event);
    if (!body) return;

    window.location.href = `data:text/calendar;charset=utf-8,${encodeURIComponent(body)}`;
};

const openLink = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
};

const CHOICES = [
    {
        key: 'google',
        label: 'Google Calendar',
        note: 'Gmail, Android or calendar.google.com',
        run: (event) => openLink(googleCalendarUrl(event))
    },
    {
        key: 'outlook',
        label: 'Outlook.com',
        note: 'Personal Hotmail, Live or Outlook account',
        run: (event) => openLink(outlookCalendarUrl(event))
    },
    {
        key: 'office',
        label: 'Outlook at work or school',
        note: 'Microsoft 365 account',
        run: (event) => openLink(outlookOfficeUrl(event))
    },
    {
        key: 'apple',
        label: 'Apple Calendar',
        note: 'iPhone, iPad or Mac',
        run: (event) => downloadIcs(event)
    },
    {
        key: 'yahoo',
        label: 'Yahoo Calendar',
        note: 'calendar.yahoo.com',
        run: (event) => openLink(yahooCalendarUrl(event))
    },
    {
        key: 'file',
        label: 'Something else',
        note: 'Downloads a calendar file you can open',
        run: (event) => downloadIcs(event)
    }
];

function AddToCalendar({ event, className = '', variant = 'button' }) {
    const [open, setOpen] = useState(false);

    const handleClick = (clickEvent) => {
        clickEvent.stopPropagation();

        const platform = detectPlatform();

        if (platform === 'ios') {
            openIcsInApp(event);
            return;
        }

        if (platform === 'android') {
            openLink(googleCalendarUrl(event));
            return;
        }

        setOpen(true);
    };

    const choose = (choice) => {
        setOpen(false);
        choice.run(event);
    };

    return (
        <>
            {variant === 'icon' ? (
                <button
                    type="button"
                    onClick={handleClick}
                    title="Add to my calendar"
                    aria-label="Add to my calendar"
                    className={`flex shrink-0 items-center justify-center border border-l-0 border-black bg-[#F8F9FA] px-[6px] shadow-[0_2px_3px_rgba(6,19,25,0.25)] transition-all hover:brightness-95 ${className}`}
                >
                    <CalendarGlyph />
                </button>
            ) : (
                <button type="button" onClick={handleClick} className={`${TRIGGER_CLASS} ${className}`}>
                    Add to my calendar
                </button>
            )}

            {open && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4"
                    onClick={(e) => { e.stopPropagation(); setOpen(false); }}
                >
                    <div
                        className="w-full max-w-md rounded-lg bg-coal p-6"
                        style={{
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                            backgroundImage: "url('/img/console-bg-darker.png')"
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="-mx-6 -mt-6 mb-4 rounded-t-lg bg-maroon px-4 py-3">
                            <h3
                                className="font-['Jersey_10'] text-[18px] uppercase tracking-[1px] text-white"
                                style={{ textShadow: '-1px -1px 0 rgba(0,0,0,0.5), 1px -1px 0 rgba(0,0,0,0.5), -1px 1px 0 rgba(0,0,0,0.5), 1px 1px 0 rgba(0,0,0,0.5)' }}
                            >
                                Which calendar do you use?
                            </h3>
                        </div>

                        <div className="mb-4 rounded bg-offwhite p-3">
                            <p className="mb-3 text-[13px] text-gray-700">
                                We will open <strong>{event.name}</strong> in your calendar with the time already set,
                                so it lands correctly wherever you are.
                            </p>

                            <div className="flex flex-col gap-2">
                                {CHOICES.map(choice => (
                                    <button
                                        key={choice.key}
                                        type="button"
                                        onClick={() => choose(choice)}
                                        className="border border-gray-300 bg-white px-3 py-2 text-left shadow-[0_2px_0_0_rgba(0,0,0,0.2)] transition-colors hover:bg-gold"
                                    >
                                        <span className="block font-['Jersey_10'] text-[17px] uppercase leading-none tracking-[1px] text-gray-800">
                                            {choice.label}
                                        </span>
                                        <span className="block text-[12px] text-gray-500">{choice.note}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="flex justify-end">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="bg-gray-500 px-4 py-2 font-['Jersey_10'] text-[16px] uppercase tracking-[1px] text-white transition-colors hover:bg-gray-600"
                                style={{ boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.3)' }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default AddToCalendar;
